<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Engagement' },
      { label: 'Invite Summary' }
    ]"
  >
    <template #right>
      <div class="flex gap-2 items-stretch lg:items-center flex-col lg:flex-row flex-wrap md:gap-4 w-full lg:w-auto">
        <DealershipSelect
          v-model="dealershipsSelected"
          :chips="false"
          mode="multiple"
          placeholder="Select a Dealership"
        />
        <div class="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          <DatePicker
            v-model="dateRange"
            mode="range"
            :presets="dateRangePresets"
          />
          <Button
            label="Update"
            :is-loading="inviteApiRequest.isLoading.value"
            :disabled="!dealershipsSelected.length"
            @click="onUpdateData"
          />
        </div>
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Invite Summary"
      :value="inviteData"
      removable-sort
      sort-field="timestamp"
      :sort-order="-1"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['dealershipId', 'dealershipName']"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-invite-summary"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="inviteApiRequest.isLoading.value"
    >
      <template #empty>
        <Empty
          v-if="!inviteApiRequest.isLoading.value && !dealershipsSelected.length"
          title="Select a dealership"
          icon="car-building"
        />
        <div v-else-if="!inviteApiRequest.isLoading.value">No records found</div>
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="dealershipName"
        header="Dealership Name"
        hidden
      />

      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
      </Column>

      <Column
        field="sentCount"
        header="Sent"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="`/reports/engagement/appointment-invites?dealership=${[data.dealershipId]}`"
            class="link"
          >
            {{ data.sentCount }}
          </router-link>
        </template>
      </Column>
      <Column
        field="openedCount"
        header="Opened"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.openedCount }}
        </template>
      </Column>
      <Column
        field="clickedCount"
        header="Link Clicked"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.clickedCount }}
        </template>
      </Column>
      <Column
        field="appointmentsBookedCount"
        header="Appointments Booked"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="`/reports/engagement/appointment-invites?dealership=${[data.dealershipId]}&appointment=${[data.appointmentsBookedCount ? true : false]}`"
            class="link"
          >
            {{ data.appointmentsBookedCount }}
          </router-link>
        </template>
      </Column>
      <Column
        field="appointmentsAttendedCount"
        header="Appointments Attended"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.appointmentsAttendedCount }}
        </template>
      </Column>
      <Column
        field="appointmentsCompletedCount"
        header="Appointments Dealt"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.appointmentsCompletedCount }}
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, watch } from 'vue'

// components
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import DealershipSelect from '@/components/forms/DealershipSelect.vue'
import Empty from '@/components/empty/Empty.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
// utilities
import { alertToast } from '@/utilities/notification'
import { datePresets } from '@/utilities/date-time'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'

const dt = ref()
const authStore = useAuthStore()
/** Dealerships the signed-in user is allowed to report on */
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)
/** Persists the page-level dealership filter between visits */
const dealershipsSelectedStorageKey = 'report-invite-summary-dealerships-selected'
/** Dealership ids currently chosen in the page header */
const dealershipsSelected = ref([])
const inviteApiRequest = useApiRequest()
const inviteData = ref([])
const filters = ref([])

const dateRange = ref({ start: dayjs().startOf('month').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') })
const dateRangePresets = [datePresets.thisWeek, datePresets.thisMonth, datePresets.lastMonth]

try {
  const stored = localStorage.getItem(dealershipsSelectedStorageKey)
  if (stored) {
    dealershipsSelected.value = JSON.parse(stored)
  }
} catch {
  dealershipsSelected.value = []
}

// Drop IDs the user no longer has access to
dealershipsSelected.value = dealershipsSelected.value.filter(id => dealershipsAllowed.value.some(d => d.id === id))

// A single allowed site can be pre-selected so the report loads immediately
if (dealershipsAllowed.value.length === 1 && dealershipsSelected.value.length === 0) {
  dealershipsSelected.value = [dealershipsAllowed.value[0].id]
}

localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(dealershipsSelected.value))

/**
 * Removes the retired column dealership filter from saved table state.
 */
function clearStoredDealershipColumnFilter() {
  try {
    const raw = sessionStorage.getItem('dt-report-invite-summary')
    if (!raw) return

    const state = JSON.parse(raw)
    // The column filter is gone, so a restored value would keep filtering with no control
    if (state?.filters?.dealershipId) {
      delete state.filters.dealershipId
      sessionStorage.setItem('dt-report-invite-summary', JSON.stringify(state))
    }
  } catch {
    sessionStorage.removeItem('dt-report-invite-summary')
  }
}
clearStoredDealershipColumnFilter()

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initFilters()

/**
 * Restores the table search filter to its default state.
 */
function clearFilters() {
  initFilters()
}

/**
 * Loads invite totals for the selected dealerships and date range.
 */
function fetchInviteData() {
  // Page-level dealership filter is required before hitting the API
  if (!dealershipsSelected.value.length) {
    inviteData.value = []
    return
  }

  try {
    inviteApiRequest
      .send({
        endpoint: 'v1/reports/engagement/invites',
        params: {
          start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value.end).format('YYYY-MM-DD'),
          dealerships: dealershipsSelected.value.join(',')
        }
      })
      .then(response => {
        if (response.success) {
          inviteData.value = response.data
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}
fetchInviteData()

/**
 * Persists the dealership selection and reloads the report.
 */
function onUpdateData() {
  localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(dealershipsSelected.value))

  // Require a complete range before refetching
  if (dateRange.value.start && dateRange.value.end) {
    fetchInviteData()
  }
}

watch(dealershipsSelected, value => {
  localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(value))
})

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
